import { useEffect, useMemo, useState } from "react";

/**
 * Route-entry budget for Position Tracker's production-sized lists. The first
 * committed frame gets enough rows to fill the normal desktop viewport, then
 * each animation frame admits one bounded batch. Data derivation still runs
 * against the complete live set; this limits React/DOM mount work only.
 */
export const INITIAL_PROGRESSIVE_ROWS = 48;
export const PROGRESSIVE_ROWS_PER_FRAME = 64;

interface ProgressiveState {
  key: string;
  limit: number;
}

export function nextProgressiveRowLimit(
  current: number,
  total: number,
  batchSize = PROGRESSIVE_ROWS_PER_FRAME,
): number {
  return Math.min(total, Math.max(0, current) + Math.max(1, batchSize));
}

/**
 * Numeric prefix budget. Kept for the orders-table window bench only: a live
 * Tracker list must use `useProgressiveAdmission`. A prefix of a list that
 * re-sorts as later reads land unmounts rows already on screen (#1331).
 */
export function useProgressiveRowLimit(
  total: number,
  resetKey: string,
): number {
  const normalizedTotal = Math.max(0, total);
  const initialLimit = Math.min(normalizedTotal, INITIAL_PROGRESSIVE_ROWS);
  const [state, setState] = useState<ProgressiveState>(() => ({
    key: resetKey,
    limit: initialLimit,
  }));

  // A changed filter/view/sort key must be bounded on that same render. Waiting
  // for the effect would briefly mount the prior list's full row count.
  const visibleLimit =
    state.key === resetKey
      ? Math.min(state.limit, normalizedTotal)
      : initialLimit;

  useEffect(() => {
    if (state.key !== resetKey) {
      const frame = window.requestAnimationFrame(() => {
        setState({ key: resetKey, limit: initialLimit });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    if (visibleLimit >= normalizedTotal) return;

    const frame = window.requestAnimationFrame(() => {
      setState((current) => {
        if (current.key !== resetKey) return current;
        return {
          key: current.key,
          limit: nextProgressiveRowLimit(current.limit, normalizedTotal),
        };
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [initialLimit, normalizedTotal, resetKey, state.key, visibleLimit]);

  return visibleLimit;
}

/** Which rows of the list passed in may mount. `mask[i]` is row i's answer. */
export interface ProgressiveAdmission {
  mask: readonly boolean[];
  /** Every row is admitted: the list renders whole (and scroll restore may run). */
  complete: boolean;
}

interface AdmissionState {
  key: string;
  /** Occurrence tokens of the rows on screen. */
  admitted: ReadonlySet<string>;
  /** Rows not yet admitted that may still mount before the next frame's grant. */
  credit: number;
  /** Counts frame grants, so the next frame is requested only after one is spent. */
  grant: number;
}

const NOTHING_ADMITTED: ReadonlySet<string> = new Set();

/**
 * #1331 — admit rows by identity, not by position. A row on screen stays on
 * screen while it is in the list, wherever a re-sort moves it; only rows not
 * yet on screen wait for budget: 48 after a reset, then up to 64 per
 * animation frame. The budget is spent, not topped up per render, so a burst
 * of renders inside one frame cannot mount more than one grant. A row that
 * leaves the list leaves the screen in the same render and must be admitted
 * again if it comes back.
 *
 * `ids` are the rows' keys in render order. Duplicated or missing ids (a
 * legacy row's `''`) are counted per occurrence: each copy is its own row and
 * costs its own budget, so rows never merge and a duplicated id never admits
 * its whole group.
 *
 * `resetKey` starts over at 48: the view, sort or filter, and the account
 * scope, so admission never carries from one sign-in, org or key mode to the
 * next.
 */
export function useProgressiveAdmission(
  ids: readonly string[],
  resetKey: string,
): ProgressiveAdmission {
  const [state, setState] = useState<AdmissionState>(() => ({
    key: resetKey,
    admitted: NOTHING_ADMITTED,
    credit: INITIAL_PROGRESSIVE_ROWS,
    grant: 0,
  }));
  const fresh = state.key !== resetKey;
  const admitted = fresh ? NOTHING_ADMITTED : state.admitted;
  const credit = fresh ? INITIAL_PROGRESSIVE_ROWS : state.credit;

  const pass = useMemo(() => {
    const occurrences = new Map<string, number>();
    const mask: boolean[] = [];
    const shown: string[] = [];
    let left = credit;
    let kept = 0;
    for (const id of ids) {
      const n = occurrences.get(id) ?? 0;
      occurrences.set(id, n + 1);
      const token = `${n}:${id}`;
      const keep = admitted.has(token);
      const admit = keep || left > 0;
      if (keep) kept += 1;
      else if (admit) left -= 1;
      mask.push(admit);
      if (admit) shown.push(token);
    }
    return {
      mask,
      shown,
      left,
      complete: shown.length === ids.length,
      // Something newly admitted, or an admitted row gone from the list.
      changed: shown.length > kept || kept !== admitted.size,
    };
  }, [ids, admitted, credit]);

  // Spend the budget before this render commits (React re-renders at once with
  // the stored state). The next pass finds every shown row admitted and changes
  // nothing.
  if (fresh || pass.changed) {
    setState({
      key: resetKey,
      admitted: new Set(pass.shown),
      credit: pass.left,
      grant: state.grant,
    });
  }

  const waiting = !pass.complete;
  useEffect(() => {
    if (!waiting) return;
    const frame = window.requestAnimationFrame(() => {
      setState((current) =>
        current.key !== resetKey
          ? current
          : {
              ...current,
              credit: PROGRESSIVE_ROWS_PER_FRAME,
              grant: current.grant + 1,
            },
      );
    });
    return () => window.cancelAnimationFrame(frame);
  }, [waiting, resetKey, state.grant]);

  return useMemo(
    () => ({ mask: pass.mask, complete: pass.complete }),
    [pass.mask, pass.complete],
  );
}

/** The admitted rows of `rows` (the list `admission` was computed for), in order. */
export function admitRows<T>(
  rows: readonly T[],
  admission: ProgressiveAdmission,
): readonly T[] {
  if (admission.complete && admission.mask.length === rows.length) return rows;
  return rows.filter((_, i) => admission.mask[i] === true);
}
